import { State } from 'flow/types/State';
import { WebsocketReadyStates } from 'flow/utils/meeting-constants';

export const selectVideoWebSocket = (state: State) =>
    state.video.get('webSocket');

export const selectVideoJoinRoomPromise = (state: State) =>
    state.video.get('joinRoomPromise');

export const selectVideoSenderVideoStream = (state: State) =>
    state.video.getIn(['sender', 'videoStream']);

export const selectVideoSecondarySenderVideoStream = (state: State) =>
    state.video.getIn(['sender', 'secondaryVideoStream']);

export const selectVideoSenderPrimaryPeer = (state: State) =>
    state.video.getIn(['sender', 'primaryPeer']);

export const selectVideoSecondarySenderPeer = (state: State) =>
    state.video.getIn(['sender', 'secondaryPeer']);

export const selectVideoSenderVideoActive = (state: State) =>
    state.video.getIn(['sender', 'videoActive']);

export const selectVideoSenderScreenShareActive = (state: State) =>
    state.video.getIn(['sender', 'screenShareActive']);

export const selectVideoSenderScreenStream = (state: State) =>
    state.video.getIn(['sender', 'screenStream']);

export const selectVideoSenderReconnectionToken = (state: State) =>
    state.video.getIn(['sender', 'reconnectionToken']);

export const selectVideoShowSenderInGridStatus = (state: State) =>
    state.video.get('showLocalVideoInGrid');

export const selectVideoShowScreenShareInGrid = (state: State) =>
    state.video.get('showScreenShareInGrid');

export const selectVideoShowIncomingVideo = (state: State) =>
    state.video.get('showIncomingVideo');

export const selectScreenShareAspect = (state: State) =>
    state.video.get('screenShareAspect');

export const selectVideoMembers = (state: State) =>
    state.video.get('videoMembers');

export const selectVideoMemberByName = (state: State, memberName: string) =>
    state.video.getIn(['videoMembers', memberName]);

export const selectPinnedFeedNames = (state: State) =>
    state.video.get('pinnedFeedNames');

export const selectFeedIsPinned = (state: State, feedName: string) =>
    state.video.get('pinnedFeedNames').has(feedName);

export const selectMemberIsSpeaking = (state: State, memberName: string) =>
    state.video.get('speakingMembers').has(memberName);

// The whole speaking set (the `.has` sibling above is for a single member).
// Drives active-speaker tile ordering — mirrors selectPinnedFeedNames.
export const selectSpeakingMembers = (state: State) =>
    state.video.get('speakingMembers');

export const selectOwnUserIsSpeaking = (state: State) =>
    state.video.get('ownUserIsSpeaking');

// Everyone asking to join (VID-76), as their join names (`<uuid>-<nick>`) — the
// same form the server keys allow/deny on. Only internal participants may act
// on them; see selectViewerIsInternal, which gates the section that shows these.
export const selectWaitingParticipants = (state: State) =>
    state.video.get('waitingParticipants');

export const selectWaitingParticipantCount = (state: State) =>
    state.video.get('waitingParticipants').size;

// Our own standing in the waiting room — see WaitingRoomStanding. Null unless the
// server held us back, so a plain truthiness check answers "am I waiting?".
export const selectOwnWaitingRoomStanding = (state: State) =>
    state.video.getIn(['ownWaitingRoom', 'state']);

export const selectOwnWaitingRoomDeniedCause = (state: State) =>
    state.video.getIn(['ownWaitingRoom', 'cause']);

export const selectVideoMemberPrimaryPeer = (
    state: State,
    memberName: string,
) => state.video.getIn(['videoMembers', memberName, 'primaryPeer']);

export const selectVideoMemberSecondaryPeer = (
    state: State,
    memberName: string,
) => state.video.getIn(['videoMembers', memberName, 'secondaryPeer']);

export const selectVideoMemberPeerSetupAttempts = (
    state: State,
    memberName: string,
) => state.video.getIn(['videoMembers', memberName, 'peerSetupAttempts']);

export const selectVideoMemberVideoActive = (
    state: State,
    memberName: string,
) => state.video.getIn(['videoMembers', memberName, 'videoActive']);

export const selectVideoMemberScreenShareActive = (
    state: State,
    memberName: string,
) => state.video.getIn(['videoMembers', memberName, 'screenShareActive']);

export const selectVideoActivityList = (state: State) =>
    state.video.get('activityList');

export const selectVideoConnectionState = (state: State) =>
    state.video.get('connectionState');

export const selectWebsocketReadyState = (state: State) => {
    const websocket = selectVideoWebSocket(state);
    return websocket && websocket.readyState;
};

export const selectWebsocketIsClosed = (state: State) => {
    const websocketState = selectWebsocketReadyState(state);
    return websocketState === WebsocketReadyStates.CLOSED;
};

export const selectVideoBackgroundBlurAmount = (state: State) => {
    return state.video.getIn(['backgroundBlur', 'blurAmount']);
};

export const selectVideoBackgroundEffectMode = (state: State) => {
    return state.video.getIn(['backgroundEffect', 'mode']);
};

export const selectVideoBackgroundImageUrl = (state: State) => {
    return state.video.getIn(['backgroundEffect', 'imageUrl']);
};
